import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { userService } from '../services/api';
import notificationService from '../services/notificationService';
import { decodeStoredUser, encodeStoredUser } from '../utils/userStorage';
import { ADMIN_ENABLED } from '../utils/adminFeature';

const AuthContext = createContext(null);

const ADMIN_IDS = [23, 25, 14746];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionReplaced, setSessionReplaced] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    // Determine user ID (real authed user only)
    // Determine user ID (real authed user only)
    const storedUser = localStorage.getItem('nizus_auth_user');
    const userId = storedUser ? decodeStoredUser(storedUser)?.id : null;

    const loadUser = async () => {
      try {
        const profile = await userService.getProfile(userId);
        // Transform backend profile to context user shape
        const contextUser = {
          id: profile.profile.id,
          name: profile.profile.firstName + ' ' + profile.profile.lastName,
          email: profile.profile.email,
          avatar: '/img/default-avatar.png',
          activeLevels: profile.activeLevels || [],
          everPurchasedLevels: profile.everPurchasedLevels || [],
          dashboardPrefs: profile.dashboardPrefs || null,
          unreadNotifications: profile.unreadNotifications || 0,
          unreadMessages: profile.unreadMessages || 0,
          ...profile.profile
        };
        setUser(contextUser);
      } catch (error) {
        console.error("Failed to load user profile:", error);
        if (error.response && error.response.status === 401) {
          // Session expired or invalid - Force logout
          localStorage.removeItem('nizus_auth_user');
          localStorage.removeItem('nizus_auth_token');
          setUser(null);
        } else {
          // Fallback to mock for offline dev (only for non-auth errors)
          if (storedUser) setUser(decodeStoredUser(storedUser));
        }
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  // Keepalive: prevent PHP session expiry while tab is open
  useEffect(() => {
    if (!user) return;
    const KEEPALIVE_MS = 10 * 60 * 1000; // 10 minutes
    const ping = async () => {
      try {
        await api.get('/auth/keepalive.php');
      } catch (err) {
        // 401 handled by interceptor; network errors silently ignored
      }
    };
    const intervalId = setInterval(ping, KEEPALIVE_MS);
    return () => clearInterval(intervalId);
  }, [user]);

  // Fetch admin preview mode status (only when admin feature is enabled)
  useEffect(() => {
    if (!ADMIN_ENABLED || !user || !ADMIN_IDS.includes(user.id)) {
      setPreviewMode(false);
      return;
    }
    import('../services/adminService').then(m => {
      m.default.checkAdmin()
        .then(r => setPreviewMode(!!r.previewMode))
        .catch(() => setPreviewMode(false));
    });
  }, [user]);

  // Listen for session_replaced from axios interceptor
  useEffect(() => {
    const handleSessionReplaced = () => {
      setUser(null);
      setSessionReplaced(true);
      localStorage.removeItem('nizus_auth_user');
      localStorage.removeItem('nizus_auth_token');
    };
    window.addEventListener('nizus:session_replaced', handleSessionReplaced);
    return () => window.removeEventListener('nizus:session_replaced', handleSessionReplaced);
  }, []);

  const login = async (email, password) => {
    try {
      const data = await userService.login(email, password);
      if (data && data.token) {
        // Success
        const contextUser = {
          ...data.user,
          activeLevels: data.user.activeLevels || [],
          everPurchasedLevels: data.user.everPurchasedLevels || [],
          dashboardPrefs: data.user.dashboardPrefs || null,
        };
        setUser(contextUser);
        // Persist minimal user info or token
        localStorage.setItem('nizus_auth_token', data.token);
        // Store user info (obfuscated)
        localStorage.setItem('nizus_auth_user', encodeStoredUser({ id: data.user.id }));

        // Fetch full profile in background (login response may lack address, phone, etc.)
        userService.getProfile(data.user.id).then(profile => {
          const fullUser = {
            id: profile.profile.id,
            name: profile.profile.firstName + ' ' + profile.profile.lastName,
            email: profile.profile.email,
            avatar: '/img/default-avatar.png',
            activeLevels: profile.activeLevels || [],
            everPurchasedLevels: profile.everPurchasedLevels || [],
            dashboardPrefs: profile.dashboardPrefs || null,
            unreadNotifications: profile.unreadNotifications || 0,
            unreadMessages: profile.unreadMessages || 0,
            ...profile.profile
          };
          setUser(fullUser);
        }).catch(() => {});

        return { success: true };
      }
    } catch (error) {
      console.error("Login failed:", error);

      let errorCode = 'LOGIN_FAILED';
      let isNetworkError = false;

      if (error.response) {
        if (error.response.status === 401) {
          errorCode = 'INVALID_CREDENTIALS';
        } else if (error.response.status === 403) {
          errorCode = 'NOT_VERIFIED';
        } else if (error.response.status >= 500) {
          errorCode = 'SERVER_ERROR';
          isNetworkError = true;
        }
      } else if (error.request) {
        errorCode = 'NETWORK_ERROR';
        isNetworkError = true;
      }

      return {
        success: false,
        errorCode,
        isNetworkError
      };
    }
  };

  const register = async (email, password, firstName = '', lastName = '', language = 'fr', referralCode = '') => {
    try {
      const data = await userService.register(email, password, firstName, lastName, language, referralCode);

      if (data && (data.token || data.success)) {
        if (data.token) {
          // Success - direct login
          const contextUser = {
            ...data.user,
            activeLevels: data.user.activeLevels || [],
            everPurchasedLevels: data.user.everPurchasedLevels || [],
            dashboardPrefs: data.user.dashboardPrefs || null,
          };
          setUser(contextUser);
          localStorage.setItem('nizus_auth_token', data.token);
          localStorage.setItem('nizus_auth_user', encodeStoredUser({ id: data.user.id }));

          // Fetch full profile in background
          userService.getProfile(data.user.id).then(profile => {
            const fullUser = {
              id: profile.profile.id,
              name: profile.profile.firstName + ' ' + profile.profile.lastName,
              email: profile.profile.email,
              avatar: '/img/default-avatar.png',
              activeLevels: profile.activeLevels || [],
              everPurchasedLevels: profile.everPurchasedLevels || [],
              dashboardPrefs: profile.dashboardPrefs || null,
              unreadNotifications: profile.unreadNotifications || 0,
              unreadMessages: profile.unreadMessages || 0,
              ...profile.profile
            };
            setUser(fullUser);
          }).catch(() => {});
        }
        return { success: true };
      }
    } catch (error) {
      console.error("AuthContext: Registration failed", error);

      let errorCode = 'REGISTER_FAILED';
      let isNetworkError = false;

      if (error.response) {
        if (error.response.status === 409) {
          errorCode = 'EMAIL_TAKEN';
        } else if (error.response.status === 400) {
          errorCode = 'INVALID_DATA';
        } else if (error.response.status >= 500) {
          errorCode = 'SERVER_ERROR';
          isNetworkError = true;
        }
      } else if (error.request) {
        errorCode = 'NETWORK_ERROR';
        isNetworkError = true;
      }

      return {
        success: false,
        errorCode,
        isNetworkError
      };
    }
  };

  const refreshNotificationCount = async () => {
    try {
      const data = await notificationService.getUnifiedUnreadCount();
      setUser(prev => prev ? { ...prev, unreadNotifications: data.count, unreadMessages: 0 } : prev);
    } catch (err) {
      // silent
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout.php');
    } catch (err) {
      // Backend call failure should not prevent local cleanup
    }
    setUser(null);
    setSessionReplaced(false);
    localStorage.removeItem('nizus_auth_user');
    localStorage.removeItem('nizus_auth_token');
  };

  return (
    <AuthContext.Provider value={{
      user,
      login,
      register,
      logout,
      loading,
      setUser, // Export setUser for profile updates
      isAuthenticated: !!user, // Helper
      isAdmin: ADMIN_ENABLED && ADMIN_IDS.includes(user?.id),
      previewMode,
      setPreviewMode,
      refreshNotificationCount,
      sessionReplaced,
      clearSessionReplaced: () => setSessionReplaced(false)
    }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
